import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Chip, Alert, CircularProgress, Button,
  Table, TableBody, TableCell, TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';

import { serverServicesAPI } from '../services/api';
import ServiceControl from './ServiceControl';
import RecentServiceActions from './RecentServiceActions';

const errText = (err, fallback) => err.response?.data?.detail || fallback;
const when = (d) => (d ? new Date(d).toLocaleString() : '-');
const ago = (d) => {
  if (!d) return '-';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  return hours < 48 ? `${hours} h ${mins % 60} min` : `${Math.floor(hours / 24)} days ${hours % 24} h`;
};

// The Report Printing Service on one server: its state, the number of jobs waiting in its jobs folder, and its config file settings (secrets are hidden on the server)
function PrintingServiceDialog({ customerCode, host, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData((await serverServicesAPI.printing(customerCode, host)).data); }
    catch (err) { setData(null); setError(errText(err, 'Could not read the service')); }
    finally { setLoading(false); }
  }, [customerCode, host]);

  useEffect(() => { load(); }, [load]);

  const pending = data?.pending;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth aria-labelledby="printing-title">
      <DialogTitle id="printing-title" sx={{ pr: 6 }}>
        Report Printing Service on {host}
        <Typography variant="body2" color="text.secondary">{data?.service || 'STARLIMS Report Printing Service'}</Typography>
        <IconButton aria-label="Close printing service" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {data && !data.found && <Alert severity="info">The Report Printing Service is not installed on this server.</Alert>}
        {data && data.found && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
              <Chip size="small" color={data.state === 'Running' ? 'success' : data.state === 'Stopped' ? 'error' : 'warning'} label={data.state} />
              {data.start_mode && <Chip size="small" variant="outlined" label={`Startup ${data.start_mode}`} />}
              {data.started && <Chip size="small" variant="outlined" label={`Up since ${when(data.started)} (${ago(data.started)})`} />}
              {data.memory_mb != null && <Chip size="small" variant="outlined" label={`Memory ${data.memory_mb} MB`} />}
              <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
              <ServiceControl customerCode={customerCode} host={host} serviceKey="printing" serviceLabel={data.service || 'STARLIMS Report Printing Service'} state={data.state} onChanged={load} />
            </Box>

            <Typography variant="subtitle2">Jobs waiting</Typography>
            {!pending.configured && <Alert severity="info" sx={{ my: 1 }}>The config file does not name a jobs folder.</Alert>}
            {pending.configured && !pending.reachable && <Alert severity="warning" sx={{ my: 1 }}>The jobs folder could not be reached from the server.</Alert>}
            {pending.reachable && (
              <Box sx={{ my: 1 }}>
                <Typography variant="h5" component="span" color={pending.count > 0 ? 'warning.main' : 'success.main'}>{pending.partial ? `${pending.count}+` : pending.count}</Typography>
                <Typography variant="body2" component="span" sx={{ ml: 1 }}>{pending.count === 1 ? 'job is' : 'jobs are'} waiting to be printed</Typography>
                {pending.count > 0 && pending.oldest && <Typography variant="body2" color="text.secondary">The oldest has been waiting {ago(pending.oldest)} (since {when(pending.oldest)}).</Typography>}
                {pending.partial && <Typography variant="caption" color="text.secondary" display="block">Counting stopped at its limit.</Typography>}
              </Box>
            )}

            <Typography variant="subtitle2" sx={{ mt: 2 }}>Config file</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }} color="text.secondary">{data.config_file}</Typography>
            {!data.config_found && <Alert severity="warning" sx={{ my: 1 }}>The config file was not found.</Alert>}
            {data.settings.length > 0 && (
              <Table size="small">
                <TableBody>
                  {data.settings.map((x) => (
                    <TableRow key={x.key}>
                      <TableCell sx={{ width: '30%', verticalAlign: 'top' }}>{x.key}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{x.value === '***' ? <Chip size="small" variant="outlined" label="hidden" /> : (x.value || '-')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Passwords and other secret settings are never shown. Read at {when(data.checked_at)}.</Typography>
          </>
        )}
        <RecentServiceActions customerCode={customerCode} host={host} refresh={data?.checked_at} />
      </DialogContent>
    </Dialog>
  );
}

export default PrintingServiceDialog;
